"use client";

import { c as _c } from "react/compiler-runtime";
import { Time } from "@internationalized/date";
import * as PopoverPrimitives from "@radix-ui/react-popover";
import { type AriaTimeFieldProps, type TimeValue, useDateSegment, useTimeField } from "@react-aria/datepicker";
import { type DateFieldState, type DateSegment, useTimeFieldState } from "@react-stately/datepicker";
import { type Locale, format, getYear, isSameMonth, setYear } from "date-fns";
import { enUS } from "date-fns/locale";
import { type VariantProps } from "tailwind-variants";
import * as React from "react";
import { useEffect } from "react";
import { useDayPicker, useNavigation } from "react-day-picker";
import { RiArrowLeftSLine, RiArrowRightSLine, RiCalendar2Fill, RiSubtractFill } from "react-icons/ri";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue } from "@/components/ui/select";
import { getBrowserLocale } from "@/lib/browser-locale";
import { tv } from "@/lib/tailwind-utils";
import { cx, focusInput, focusRing, hasErrorInput } from "@/lib/tailwind-utils";
import { matchingOccurrence, wallClockDateToInstant, zonedWallClockDate } from "@/lib/timezone-dates";
import Button from "./button";
import { Calendar as CalendarPrimitive, type Matcher } from "./calendar";
import Input from "./input";

// Tremor Date Picker [v2.0.0]

//#region NavigationButton
// ============================================================================

interface NavigationButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  onClick: () => void;
  icon: React.ElementType<{
    className?: string;
  }>;
  disabled?: boolean;
}
const NavigationButton = React.forwardRef<HTMLButtonElement, NavigationButtonProps>((t0, forwardedRef) => {
  const $ = _c(14);
  let disabled;
  let icon;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      onClick,
      icon,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = disabled;
    $[2] = icon;
    $[3] = onClick;
    $[4] = props;
  } else {
    disabled = $[1];
    icon = $[2];
    onClick = $[3];
    props = $[4];
  }
  const Icon = icon;
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx("flex size-8 shrink-0 select-none items-center justify-center rounded border p-1 outline-none transition sm:size-[30px]", "text-foreground-secondary hover:text-foreground", "hover:bg-row-hover active:bg-hover", "disabled:pointer-events-none", focusRing);
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== Icon) {
    t2 = <Icon className="size-full shrink-0" />;
    $[6] = Icon;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== disabled || $[9] !== forwardedRef || $[10] !== onClick || $[11] !== props || $[12] !== t2) {
    t3 = <button ref={forwardedRef} type="button" disabled={disabled} className={t1} onClick={onClick} {...props}>{t2}</button>;
    $[8] = disabled;
    $[9] = forwardedRef;
    $[10] = onClick;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
});
NavigationButton.displayName = "NavigationButton";

//#region TimeInput
// ============================================================================

const isBrowserLocaleClockType24h = () => {
  const hr = new Intl.DateTimeFormat(getBrowserLocale(), {
    hour: "numeric"
  }).format();
  return Number.isInteger(Number(hr));
};
type TimeSegmentProps = {
  segment: DateSegment;
  state: DateFieldState;
};
const TimeSegment = t0 => {
  const $ = _c(7);
  const {
    segment,
    state
  } = t0;
  const ref = React.useRef(null);
  const {
    segmentProps
  } = useDateSegment(segment, state, ref);
  if (!segment.isEditable && segment.type === "literal" && segment.text !== ":") {
    return null;
  }
  const t1 = segment.type === "literal";
  const t2 = state.isDisabled && segment.text !== ":";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cx("relative block w-full appearance-none rounded-md border px-2.5 py-1.5 text-left uppercase tabular-nums shadow-sm outline-none transition sm:text-body", "text-foreground", "bg-background/80 backdrop-blur-sm", focusInput, "group-aria-invalid/time-input:border-status-error-text group-aria-invalid/time-input:ring-2 group-aria-invalid/time-input:ring-status-error-bg invalid:border-status-error-text invalid:ring-2 invalid:ring-status-error-bg", {
      "w-fit! border-none bg-transparent px-0 text-foreground-muted shadow-none": t1,
      "border-border bg-muted text-foreground-muted": t2
    });
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = segment.isPlaceholder ? segment.placeholder : segment.text;
  let t5;
  if ($[3] !== segmentProps || $[4] !== t3 || $[5] !== t4) {
    t5 = <div {...segmentProps} ref={ref} className={t3}>{t4}</div>;
    $[3] = segmentProps;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
type TimeInputProps = Omit<AriaTimeFieldProps<TimeValue>, "label" | "shouldForceLeadingZeros" | "description" | "errorMessage">;
const TimeInput = React.forwardRef<HTMLDivElement, TimeInputProps>((t0, ref) => {
  const $ = _c(16);
  let hourCycle;
  let props;
  if ($[0] !== t0) {
    ({
      hourCycle,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = hourCycle;
    $[2] = props;
  } else {
    hourCycle = $[1];
    props = $[2];
  }
  const innerRef = React.useRef(null);
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => innerRef?.current;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  React.useImperativeHandle(ref, t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getBrowserLocale();
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const locale = t2;
  let t3;
  if ($[5] !== hourCycle || $[6] !== props) {
    t3 = {
      hourCycle,
      locale,
      shouldForceLeadingZeros: true,
      autoFocus: true,
      ...props
    };
    $[5] = hourCycle;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const state = useTimeFieldState(t3);
  let t4;
  if ($[8] !== hourCycle || $[9] !== props) {
    t4 = {
      ...props,
      hourCycle,
      shouldForceLeadingZeros: true
    };
    $[8] = hourCycle;
    $[9] = props;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    fieldProps
  } = useTimeField(t4, state, innerRef);
  let t5;
  if ($[11] !== state) {
    t5 = state.segments.map((segment, i) => <TimeSegment key={i} segment={segment} state={state} />);
    $[11] = state;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== fieldProps || $[14] !== t5) {
    t6 = <div {...fieldProps} ref={innerRef} className="group/time-input inline-flex w-full gap-x-2">{t5}</div>;
    $[13] = fieldProps;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
});
TimeInput.displayName = "TimeInput";

//#region Trigger
// ============================================================================

const triggerStyles = tv({
  base: [
  // base
  "peer flex w-full cursor-pointer appearance-none items-center gap-x-2 truncate rounded-md border px-3 py-2 shadow-sm outline-none transition-all sm:text-body",
  // background color
  "bg-background/80 backdrop-blur-sm",
  // border color

  // text color
  "text-foreground",
  // placeholder color
  "placeholder-foreground-muted",
  // hover
  "hover:bg-row-hover",
  // disabled
  "disabled:pointer-events-none", "disabled:bg-muted disabled:text-foreground-muted",
  // focus
  focusInput
  // invalid (optional)
  // "aria-invalid:ring-2 aria-invalid:ring-red-200 aria-invalid:border-red-500 invalid:ring-2 invalid:ring-red-200 invalid:border-red-500"
  ],
  variants: {
    hasError: {
      true: hasErrorInput
    }
  }
});
interface TriggerProps extends React.ComponentProps<"button">, VariantProps<typeof triggerStyles> {
  placeholder?: string;
}
const Trigger = React.forwardRef<HTMLButtonElement, TriggerProps>((t0, forwardedRef) => {
  const $ = _c(20);
  let children;
  let className;
  let hasError;
  let placeholder;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      placeholder,
      hasError,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = hasError;
    $[4] = placeholder;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    hasError = $[3];
    placeholder = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== hasError) {
    t1 = cx(triggerStyles({
      hasError
    }), className);
    $[6] = className;
    $[7] = hasError;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RiCalendar2Fill className="size-5 shrink-0 text-foreground-muted" />;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== children || $[11] !== placeholder) {
    t3 = children ? children : placeholder ? <span className="text-foreground-muted">{placeholder}</span> : null;
    $[10] = children;
    $[11] = placeholder;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t3) {
    t4 = <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left text-foreground">{t3}</span>;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== forwardedRef || $[16] !== props || $[17] !== t1 || $[18] !== t4) {
    t5 = <PopoverPrimitives.Trigger asChild={true}><button ref={forwardedRef} type="button" className={t1} {...props}>{t2}{t4}</button></PopoverPrimitives.Trigger>;
    $[15] = forwardedRef;
    $[16] = props;
    $[17] = t1;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
});
Trigger.displayName = "DatePicker.Trigger";

//#region TypeableTrigger
// ============================================================================

// The years the calendar's year dropdown lists; typed years stay inside it so
// the calendar can always show what was typed.
const selectableYears = () => {
  const currentYear = getYear(new Date());
  return {
    first: currentYear - 120,
    last: currentYear + 19
  };
};

// Parses a typed date: MM/DD/YYYY, MM/DD/YY, MM/DD, or bare digits MMDDYYYY /
// MMDDYY. Shorter forms are prefixes of longer ones ("08/01/20" on the way to
// "08/01/2028"), so while the user is still typing only a four-digit year
// counts; the short forms are accepted once they finish (blur, Enter, Tab).
// Limits such as "not after today" belong to the caller's minDate/maxDate.
const parseTypedDate = (input: string, {
  complete
}: {
  complete: boolean;
}): Date | null => {
  if (!input || typeof input !== "string") return null;
  const cleanInput = input.trim();
  if (!cleanInput) return null;
  const match = cleanInput.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?$/) ?? cleanInput.match(/^(\d{2})(\d{2})(\d{4}|\d{2})$/);
  if (!match) return null;
  const month = parseInt(match[1], 10);
  const day = parseInt(match[2], 10);
  const yearText = match[3];
  if (yearText?.length !== 4 && !complete) return null;
  let year: number;
  if (!yearText) {
    year = new Date().getFullYear();
  } else if (yearText.length === 2) {
    // Assume 1900s for YY >= 50, 2000s for YY < 50
    const yy = parseInt(yearText, 10);
    year = yy >= 50 ? 1900 + yy : 2000 + yy;
  } else {
    year = parseInt(yearText, 10);
  }
  const {
    first,
    last
  } = selectableYears();
  if (year < first || year > last) return null;
  const date = new Date(year, month - 1, day, 12, 0, 0);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
};
interface TypeableTriggerProps extends Omit<React.ComponentProps<"input">, "onChange" | "value"> {
  placeholder?: string;
  hasError?: boolean;
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  onOpenCalendar?: () => void;
}
const TypeableTrigger = React.forwardRef<HTMLInputElement, TypeableTriggerProps>((t0, forwardedRef) => {
  const $ = _c(45);
  let className;
  let hasError;
  let onChange;
  let onOpenCalendar;
  let placeholder;
  let props;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      placeholder,
      hasError,
      value,
      onChange,
      onOpenCalendar,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = hasError;
    $[3] = onChange;
    $[4] = onOpenCalendar;
    $[5] = placeholder;
    $[6] = props;
    $[7] = value;
  } else {
    className = $[1];
    hasError = $[2];
    onChange = $[3];
    onOpenCalendar = $[4];
    placeholder = $[5];
    props = $[6];
    value = $[7];
  }
  const [inputValue, setInputValue] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const cancelledRef = React.useRef(false);
  let t1;
  bb0: {
    if (!value || isNaN(value.getTime())) {
      t1 = "";
      break bb0;
    }
    let t2;
    if ($[8] !== value) {
      t2 = format(value, "MM/dd/yyyy");
      $[8] = value;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t1 = t2;
  }
  const formattedDate = t1;
  let t2;
  let t3;
  if ($[10] !== formattedDate || $[11] !== isFocused) {
    t2 = () => {
      if (!isFocused) {
        setInputValue(formattedDate);
      }
    };
    t3 = [formattedDate, isFocused];
    $[10] = formattedDate;
    $[11] = isFocused;
    $[12] = t2;
    $[13] = t3;
  } else {
    t2 = $[12];
    t3 = $[13];
  }
  React.useEffect(t2, t3);
  let t4;
  if ($[14] !== onChange) {
    t4 = e => {
      const newValue = e.target.value;
      setInputValue(newValue);
      const parsedDate = parseTypedDate(newValue, {
        complete: false
      });
      if (parsedDate) {
        onChange?.(parsedDate);
      }
    };
    $[14] = onChange;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleInputChange = t4;
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsFocused(true);
    };
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleFocus = t5;
  let t6;
  if ($[17] !== formattedDate || $[18] !== inputValue || $[19] !== onChange || $[20] !== value) {
    t6 = () => {
      setIsFocused(false);
      if (cancelledRef.current) {
        cancelledRef.current = false;
        return;
      }
      const parsedDate_0 = parseTypedDate(inputValue, {
        complete: true
      });
      if (parsedDate_0) {
        if (format(parsedDate_0, "MM/dd/yyyy") !== formattedDate) {
          onChange?.(parsedDate_0);
        }
      } else {
        if (value) {
          setInputValue(formattedDate);
        } else {
          setInputValue("");
        }
      }
    };
    $[17] = formattedDate;
    $[18] = inputValue;
    $[19] = onChange;
    $[20] = value;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleBlur = t6;
  let t7;
  if ($[22] !== formattedDate || $[23] !== inputValue || $[24] !== onChange || $[25] !== onOpenCalendar) {
    t7 = e_0 => {
      if (e_0.key === "Enter") {
        const parsedDate_1 = parseTypedDate(inputValue, {
          complete: true
        });
        if (parsedDate_1) {
          onChange?.(parsedDate_1);
        }
      } else {
        if (e_0.key === "Escape") {
          setInputValue(formattedDate);
          cancelledRef.current = true;
          e_0.currentTarget.blur();
        } else {
          if (e_0.key === "ArrowDown" || e_0.key === " " && !inputValue) {
            e_0.preventDefault();
            onOpenCalendar?.();
          }
        }
      }
    };
    $[22] = formattedDate;
    $[23] = inputValue;
    $[24] = onChange;
    $[25] = onOpenCalendar;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const handleKeyDown = t7;
  const t8 = hasError && "border-status-error-text focus:border-status-error-text focus:ring-status-error-bg";
  let t9;
  if ($[27] !== className || $[28] !== t8) {
    t9 = cx("pr-10 text-left", t8, className);
    $[27] = className;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== forwardedRef || $[31] !== handleBlur || $[32] !== handleInputChange || $[33] !== handleKeyDown || $[34] !== inputValue || $[35] !== placeholder || $[36] !== props || $[37] !== t9) {
    t10 = <Input ref={forwardedRef} {...props} type="text" value={inputValue} onChange={handleInputChange} onFocus={handleFocus} onBlur={handleBlur} onKeyDown={handleKeyDown} placeholder={placeholder} className={t9} />;
    $[30] = forwardedRef;
    $[31] = handleBlur;
    $[32] = handleInputChange;
    $[33] = handleKeyDown;
    $[34] = inputValue;
    $[35] = placeholder;
    $[36] = props;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <RiCalendar2Fill className="size-5 shrink-0" />;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== onOpenCalendar) {
    t12 = <button type="button" onClick={onOpenCalendar} className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-muted hover:text-foreground-secondary" tabIndex={-1}>{t11}</button>;
    $[40] = onOpenCalendar;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== t10 || $[43] !== t12) {
    t13 = <div className="relative">{t10}{t12}</div>;
    $[42] = t10;
    $[43] = t12;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
});
TypeableTrigger.displayName = "DatePicker.TypeableTrigger";

//#region Popover
// ============================================================================

const CalendarPopover = React.forwardRef<React.ElementRef<typeof PopoverPrimitives.Content>, React.ComponentProps<typeof PopoverPrimitives.Content>>((t0, forwardedRef) => {
  const $ = _c(13);
  let align;
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      align,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = align;
    $[2] = children;
    $[3] = className;
    $[4] = props;
  } else {
    align = $[1];
    children = $[2];
    className = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cx("relative z-50 w-fit rounded-md border text-body shadow-xl shadow-black/[2.5%]", "min-w-[calc(var(--radix-select-trigger-width)-2px)] max-w-[95vw]", "max-h-[var(--radix-popover-content-available-height)] overflow-y-auto", "border-border", "bg-background/80 backdrop-blur-sm", "will-change-[transform,opacity]", "data-[state=closed]:animate-hide", "data-[state=open]:data-[side=bottom]:animate-slideDownAndFade data-[state=open]:data-[side=left]:animate-slideLeftAndFade data-[state=open]:data-[side=right]:animate-slideRightAndFade data-[state=open]:data-[side=top]:animate-slideUpAndFade", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== align || $[8] !== children || $[9] !== forwardedRef || $[10] !== props || $[11] !== t1) {
    t2 = <PopoverPrimitives.Portal><PopoverPrimitives.Content ref={forwardedRef} sideOffset={10} side="bottom" align={align} avoidCollisions={true} collisionPadding={8} onOpenAutoFocus={_temp} className={t1} {...props}>{children}</PopoverPrimitives.Content></PopoverPrimitives.Portal>;
    $[7] = align;
    $[8] = children;
    $[9] = forwardedRef;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
});
CalendarPopover.displayName = "DatePicker.CalendarPopover";

//#region Preset
// ============================================================================

type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
};
interface Preset {
  label: string;
}
interface DatePreset extends Preset {
  date: Date;
}
interface DateRangePreset extends Preset {
  dateRange: DateRange;
}
type PresetContainerProps<TPreset extends Preset, TValue> = {
  presets: TPreset[];
  onSelect: (value: TValue) => void;
  currentValue?: TValue;
};
const PresetContainer = t0 => {
  const $ = _c(14);
  const {
    presets,
    onSelect,
    currentValue
  } = t0;
  const isDateRangePresets = _temp2;
  const isDatePresets = _temp3;
  let t1;
  if ($[0] !== onSelect) {
    t1 = preset_1 => {
      if (isDateRangePresets(preset_1)) {
        onSelect(preset_1.dateRange as TValue);
      } else {
        if (isDatePresets(preset_1)) {
          onSelect(preset_1.date as TValue);
        }
      }
    };
    $[0] = onSelect;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClick = t1;
  const compareDates = _temp4;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (range1, range2) => {
      const from1 = range1.from;
      const from2 = range2.from;
      let equalFrom = false;
      if (from1 && from2) {
        const sameFrom = compareDates(from1, from2);
        if (sameFrom) {
          equalFrom = true;
        }
      }
      const to1 = range1.to;
      const to2 = range2.to;
      let equalTo = false;
      if (to1 && to2) {
        const sameTo = compareDates(to1, to2);
        if (sameTo) {
          equalTo = true;
        }
      }
      return equalFrom && equalTo;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const compareRanges = t2;
  let t3;
  if ($[3] !== currentValue) {
    t3 = preset_2 => {
      if (isDateRangePresets(preset_2)) {
        const value = currentValue as DateRange | undefined;
        return value && compareRanges(value, preset_2.dateRange);
      } else {
        if (isDatePresets(preset_2)) {
          const value_0 = currentValue as Date | undefined;
          return value_0 && compareDates(value_0, preset_2.date);
        }
      }
      return false;
    };
    $[3] = currentValue;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const matchesCurrent = t3;
  let t4;
  if ($[5] !== handleClick || $[6] !== matchesCurrent || $[7] !== presets) {
    let t5;
    if ($[9] !== handleClick || $[10] !== matchesCurrent) {
      t5 = preset_3 => <li key={`preset-${preset_3.label}`} className="sm:w-full sm:py-px"><button type="button" title={preset_3.label} className={cx("relative w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-sm border px-2.5 py-1.5 text-left text-body-lg shadow-sm outline-none transition-all sm:border-none sm:py-2 sm:text-body sm:shadow-none", "text-foreground-secondary", "border-border", focusRing, "focus-visible:bg-hover", "hover:bg-hover", {
          "bg-hover": matchesCurrent(preset_3)
        })} onClick={() => handleClick(preset_3)} aria-label={`Select ${preset_3.label}`}><span>{preset_3.label}</span></button></li>;
      $[9] = handleClick;
      $[10] = matchesCurrent;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = presets.map(t5);
    $[5] = handleClick;
    $[6] = matchesCurrent;
    $[7] = presets;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <ul className="flex items-start gap-x-2 sm:flex-col">{t4}</ul>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
PresetContainer.displayName = "DatePicker.PresetContainer";

//#region Date Picker Shared
// ============================================================================

const formatDate = (date: Date, locale: Locale, includeTime?: boolean): string => {
  const usesAmPm = !isBrowserLocaleClockType24h();
  let dateString: string;
  if (includeTime) {
    dateString = usesAmPm ? format(date, "dd MMM, yyyy h:mm a", {
      locale
    }) : format(date, "dd MMM, yyyy HH:mm", {
      locale
    });
  } else {
    dateString = format(date, "dd MMM, yyyy", {
      locale
    });
  }
  return dateString;
};
type CalendarProps = {
  fromYear?: number;
  toYear?: number;
  fromMonth?: Date;
  toMonth?: Date;
  fromDay?: Date;
  toDay?: Date;
  fromDate?: Date;
  toDate?: Date;
  locale?: Locale;
};
type Translations = {
  cancel?: string;
  apply?: string;
  start?: string;
  end?: string;
  range?: string;
};
interface PickerProps extends CalendarProps {
  className?: string;
  disabled?: boolean;
  disabledDays?: Matcher | Matcher[] | undefined;
  required?: boolean;
  showTimePicker?: boolean;
  placeholder?: string;
  enableYearNavigation?: boolean;
  disableNavigation?: boolean;
  hasError?: boolean;
  id?: string;
  // Customize the date picker for different languages.
  translations?: Translations;
  align?: "center" | "end" | "start";
  "aria-invalid"?: boolean;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-required"?: boolean;
}

//#region Single Date Picker
// ============================================================================

interface SingleProps extends Omit<PickerProps, "translations"> {
  presets?: DatePreset[];
  defaultValue?: Date;
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  onPopoverOpenChange?: (open: boolean) => void;
  translations?: Omit<Translations, "range">;
}
const SingleDatePicker = ({
  defaultValue,
  value,
  onChange,
  onPopoverOpenChange,
  presets,
  disabled,
  disabledDays,
  disableNavigation,
  className,
  showTimePicker,
  placeholder = "Select date",
  hasError,
  translations,
  enableYearNavigation = false,
  locale = enUS,
  align = "center",
  ...props
}: SingleProps) => {
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState<Date | undefined>(value ?? defaultValue ?? undefined);
  const [month, setMonth] = React.useState<Date | undefined>(date);
  const [time, setTime] = React.useState<TimeValue | null>(value ? new Time(value.getHours(), value.getMinutes()) : defaultValue ? new Time(defaultValue.getHours(), defaultValue.getMinutes()) : new Time(0, 0));
  const initialDate = React.useMemo(() => {
    return date;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  React.useEffect(() => {
    setDate(value ?? defaultValue ?? undefined);
  }, [value, defaultValue]);
  React.useEffect(() => {
    if (date) {
      setMonth(date);
    }
  }, [date]);
  React.useEffect(() => {
    if (!open) {
      setMonth(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const closePopover = () => {
    setOpen(false);
    onPopoverOpenChange?.(false);
  };
  const onCancel = () => {
    setDate(initialDate);
    setTime(initialDate ? new Time(initialDate.getHours(), initialDate.getMinutes()) : new Time(0, 0));
    closePopover();
  };
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onCancel();
      return;
    }
    setOpen(true);
    onPopoverOpenChange?.(true);
  };
  const onDateChange = (date: Date | undefined) => {
    const newDate = date;
    if (showTimePicker) {
      if (newDate && !time) {
        setTime(new Time(0, 0));
      }
      if (newDate && time) {
        newDate.setHours(time.hour);
        newDate.setMinutes(time.minute);
      }
    }
    setDate(newDate);
  };
  const onTimeChange = (time: TimeValue | null) => {
    setTime(time);
    if (!date) {
      return;
    }
    const newDate = new Date(date.getTime());
    if (!time) {
      newDate.setHours(0);
      newDate.setMinutes(0);
    } else {
      newDate.setHours(time.hour);
      newDate.setMinutes(time.minute);
    }
    setDate(newDate);
  };
  const formattedDate = React.useMemo(() => {
    if (!date) {
      return null;
    }
    return formatDate(date, locale, showTimePicker);
  }, [date, locale, showTimePicker]);
  const onApply = () => {
    closePopover();
    onChange?.(date);
  };
  React.useEffect(() => {
    setDate(value ?? defaultValue ?? undefined);
    setTime(value ? new Time(value.getHours(), value.getMinutes()) : defaultValue ? new Time(defaultValue.getHours(), defaultValue.getMinutes()) : new Time(0, 0));
  }, [value, defaultValue]);
  return <PopoverPrimitives.Root open={open} onOpenChange={handleOpenChange} modal>
      <Trigger placeholder={placeholder} disabled={disabled} className={className} hasError={hasError} aria-required={props.required || props["aria-required"]} aria-invalid={props["aria-invalid"]} aria-label={props["aria-label"]} aria-labelledby={props["aria-labelledby"]}>
        {formattedDate}
      </Trigger>
      <CalendarPopover align={align}>
        <div className="flex">
          <div className="flex flex-col sm:flex-row sm:items-start">
            {presets && presets.length > 0 && <div className={cx("relative flex h-14 w-full items-center sm:h-full sm:w-40", "border-b sm:border-b-0 sm:border-r", "overflow-auto")}>
                <div className="absolute px-2 pr-2 sm:inset-0 sm:left-0 sm:py-2">
                  <PresetContainer currentValue={date} presets={presets} onSelect={onDateChange} />
                </div>
              </div>}
            <div>
              <CalendarPrimitive mode="single" month={month} onMonthChange={setMonth} selected={date} onSelect={onDateChange} disabled={disabledDays} locale={locale} enableYearNavigation={enableYearNavigation} disableNavigation={disableNavigation} initialFocus {...props} />
              {showTimePicker && <div className="border-t p-3">
                  <TimeInput aria-label="Time" onChange={onTimeChange} isDisabled={!date} value={time} isRequired={props.required} />
                </div>}
              <div className="sticky bottom-0 flex items-center gap-x-2 border-t bg-background p-3">
                <Button variant="secondary" className="h-8 w-full" type="button" onClick={onCancel}>
                  {translations?.cancel ?? "Cancel"}
                </Button>
                <Button variant="primary" className="h-8 w-full" type="button" onClick={onApply}>
                  {translations?.apply ?? "Apply"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CalendarPopover>
    </PopoverPrimitives.Root>;
};

//#region Range Date Picker
// ============================================================================

interface RangeProps extends PickerProps {
  presets?: DateRangePreset[];
  defaultValue?: DateRange;
  value?: DateRange;
  onChange?: (dateRange: DateRange | undefined) => void;
  ignoreYear?: boolean;
  max?: number;
}
const maybeBumpYear = (range: DateRange | undefined, ignoreYear: boolean): DateRange | undefined => {
  if (!ignoreYear || !range?.from || !range?.to || range.from <= range.to) {
    return range;
  }
  const bumpedTo = new Date(range.to);
  bumpedTo.setFullYear(range.to.getFullYear() + 1);
  return {
    from: range.from,
    to: bumpedTo
  };
};
const MonthOnlyCaption = t0 => {
  const $ = _c(26);
  const {
    displayMonth
  } = t0;
  const {
    goToMonth,
    nextMonth,
    previousMonth,
    displayMonths
  } = useNavigation();
  const {
    numberOfMonths,
    locale
  } = useDayPicker();
  let t1;
  if ($[0] !== displayMonth || $[1] !== displayMonths) {
    let t2;
    if ($[3] !== displayMonth) {
      t2 = month => isSameMonth(displayMonth, month);
      $[3] = displayMonth;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = displayMonths.findIndex(t2);
    $[0] = displayMonth;
    $[1] = displayMonths;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const displayIndex = t1;
  const isFirst = displayIndex === 0;
  const isLast = displayIndex === displayMonths.length - 1;
  const hideNextButton = numberOfMonths > 1 && (isFirst || !isLast);
  const hidePreviousButton = numberOfMonths > 1 && (isLast || !isFirst);
  let t2;
  if ($[5] !== goToMonth || $[6] !== hidePreviousButton || $[7] !== previousMonth) {
    t2 = !hidePreviousButton && <NavigationButton disabled={!previousMonth} aria-label="Go to previous month" onClick={() => previousMonth && goToMonth(previousMonth)} icon={RiArrowLeftSLine} />;
    $[5] = goToMonth;
    $[6] = hidePreviousButton;
    $[7] = previousMonth;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <div className="flex items-center gap-1">{t2}</div>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== displayMonth || $[12] !== locale) {
    t4 = format(displayMonth, "LLLL", {
      locale
    });
    $[11] = displayMonth;
    $[12] = locale;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <div role="presentation" aria-live="polite" className="px-2 text-body-strong capitalize tabular-nums text-foreground">{t4}</div>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== goToMonth || $[17] !== hideNextButton || $[18] !== nextMonth) {
    t6 = !hideNextButton && <NavigationButton disabled={!nextMonth} aria-label="Go to next month" onClick={() => nextMonth && goToMonth(nextMonth)} icon={RiArrowRightSLine} />;
    $[16] = goToMonth;
    $[17] = hideNextButton;
    $[18] = nextMonth;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = <div className="flex items-center gap-1">{t6}</div>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t5 || $[24] !== t7) {
    t8 = <div className="flex items-center justify-between">{t3}{t5}{t7}</div>;
    $[22] = t3;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};
const RangeDatePicker = ({
  defaultValue,
  value,
  onChange,
  presets,
  disabled,
  disableNavigation,
  disabledDays,
  enableYearNavigation = false,
  ignoreYear = false,
  locale = enUS,
  showTimePicker,
  placeholder = "Select date range",
  hasError,
  translations,
  align = "center",
  className,
  ...props
}: RangeProps) => {
  const [open, setOpen] = React.useState(false);
  const [range, setRange] = React.useState<DateRange | undefined>(maybeBumpYear(value ?? defaultValue, ignoreYear));
  const [month, setMonth] = React.useState<Date | undefined>(range?.from);
  const [startTime, setStartTime] = React.useState<TimeValue | null>(value?.from ? new Time(value.from.getHours(), value.from.getMinutes()) : defaultValue?.from ? new Time(defaultValue.from.getHours(), defaultValue.from.getMinutes()) : new Time(0, 0));
  const [endTime, setEndTime] = React.useState<TimeValue | null>(value?.to ? new Time(value.to.getHours(), value.to.getMinutes()) : defaultValue?.to ? new Time(defaultValue.to.getHours(), defaultValue.to.getMinutes()) : new Time(0, 0));
  const initialRange = React.useMemo(() => {
    return range;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  React.useEffect(() => {
    setRange(maybeBumpYear(value ?? defaultValue, ignoreYear));
  }, [value, defaultValue, ignoreYear]);
  React.useEffect(() => {
    if (range) {
      setMonth(range.from);
    }
  }, [range]);
  React.useEffect(() => {
    if (!open) {
      setMonth(range?.from);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const onRangeChange = (next: DateRange | undefined, selectedDay?: Date) => {
    // react-day-picker grows a complete range from its nearest end, so clicking
    // 6 then 8 on "1–15" yields 1–8. Once both ends are set, a calendar click
    // starts a fresh range instead. Presets pass no day and apply as-is.
    const newRange = selectedDay && range?.from && range?.to ? {
      from: selectedDay,
      to: undefined
    } : next;
    if (showTimePicker) {
      if (newRange?.from && !startTime) {
        setStartTime(new Time(0, 0));
      }
      if (newRange?.to && !endTime) {
        setEndTime(new Time(0, 0));
      }
      if (newRange?.from && startTime) {
        newRange.from.setHours(startTime.hour);
        newRange.from.setMinutes(startTime.minute);
      }
      if (newRange?.to && endTime) {
        newRange.to.setHours(endTime.hour);
        newRange.to.setMinutes(endTime.minute);
      }
    }
    setRange(newRange);
  };
  const onCancel = () => {
    setRange(initialRange);
    setStartTime(initialRange?.from ? new Time(initialRange.from.getHours(), initialRange.from.getMinutes()) : new Time(0, 0));
    setEndTime(initialRange?.to ? new Time(initialRange.to.getHours(), initialRange.to.getMinutes()) : new Time(0, 0));
    setOpen(false);
  };
  const onOpenChange = (open: boolean) => {
    if (!open) {
      onCancel();
    }
    setOpen(open);
  };
  const onTimeChange = (time: TimeValue | null, pos: "start" | "end") => {
    switch (pos) {
      case "start":
        setStartTime(time);
        break;
      case "end":
        setEndTime(time);
        break;
    }
    if (!range) {
      return;
    }
    if (pos === "start") {
      if (!range.from) {
        return;
      }
      const newDate = new Date(range.from.getTime());
      if (!time) {
        newDate.setHours(0);
        newDate.setMinutes(0);
      } else {
        newDate.setHours(time.hour);
        newDate.setMinutes(time.minute);
      }
      setRange({
        ...range,
        from: newDate
      });
    }
    if (pos === "end") {
      if (!range.to) {
        return;
      }
      const newDate = new Date(range.to.getTime());
      if (!time) {
        newDate.setHours(0);
        newDate.setMinutes(0);
      } else {
        newDate.setHours(time.hour);
        newDate.setMinutes(time.minute);
      }
      setRange({
        ...range,
        to: newDate
      });
    }
  };
  React.useEffect(() => {
    setRange(maybeBumpYear(value ?? defaultValue, ignoreYear));
    setStartTime(value?.from ? new Time(value.from.getHours(), value.from.getMinutes()) : defaultValue?.from ? new Time(defaultValue.from.getHours(), defaultValue.from.getMinutes()) : new Time(0, 0));
    setEndTime(value?.to ? new Time(value.to.getHours(), value.to.getMinutes()) : defaultValue?.to ? new Time(defaultValue.to.getHours(), defaultValue.to.getMinutes()) : new Time(0, 0));
  }, [value, defaultValue, ignoreYear]);
  const displayRange = React.useMemo(() => {
    if (!range) {
      return null;
    }
    const formatter = (date: Date) => ignoreYear ? format(date, "MMM d", {
      locale
    }) : formatDate(date, locale, showTimePicker);
    return `${range.from ? formatter(range.from) : ""} - ${range.to ? formatter(range.to) : ""}`;
  }, [range, locale, showTimePicker, ignoreYear]);
  const onApply = () => {
    setOpen(false);
    // One click on a complete range leaves only a start day; apply it as that
    // single day rather than an open-ended range.
    const applied = range?.from && !range.to ? {
      from: range.from,
      to: new Date(range.from.getTime())
    } : range;
    setRange(applied);
    onChange?.(applied);
  };
  return <PopoverPrimitives.Root open={open} onOpenChange={onOpenChange} modal>
      <Trigger placeholder={placeholder} disabled={disabled} className={className} hasError={hasError} aria-required={props.required || props["aria-required"]} aria-invalid={props["aria-invalid"]} aria-label={props["aria-label"]} aria-labelledby={props["aria-labelledby"]}>
        {displayRange}
      </Trigger>
      <CalendarPopover align={align}>
        <div className="flex">
          <div className="flex min-w-0 flex-col sm:flex-row sm:items-start">
            {presets && presets.length > 0 && <div className={cx("relative flex h-16 w-full items-center sm:h-full sm:w-40", "border-b sm:border-b-0 sm:border-r", "overflow-auto")}>
                <div className="absolute px-3 sm:inset-0 sm:left-0 sm:p-2">
                  <PresetContainer currentValue={range} presets={presets} onSelect={onRangeChange} />
                </div>
              </div>}
            {/* No overflow here: it would trap the sticky footer below. The
                months row scrolls sideways on its own. */}
            <div className="min-w-0">
              <CalendarPrimitive mode="range" selected={range} onSelect={onRangeChange} month={month} onMonthChange={setMonth} numberOfMonths={2} disabled={disabledDays} disableNavigation={disableNavigation} enableYearNavigation={ignoreYear ? false : enableYearNavigation} locale={locale} initialFocus classNames={{
              months: "flex flex-row divide-x divide-border overflow-x-auto"
            }} {...props} components={ignoreYear ? {
              Caption: MonthOnlyCaption
            } : undefined} />
              {showTimePicker && <div className="flex items-center justify-evenly gap-x-3 border-t p-3">
                  <div className="flex flex-1 items-center gap-x-2">
                    <span className="text-foreground-secondary">
                      <span>{translations?.start ?? "Start"}</span>
                      <span>:</span>
                    </span>
                    <TimeInput value={startTime} onChange={v => onTimeChange(v, "start")} aria-label="Start date time" isDisabled={!range?.from} isRequired={props.required} />
                  </div>
                  <RiSubtractFill className="size-4 shrink-0 text-foreground-muted" />
                  <div className="flex flex-1 items-center gap-x-2">
                    <span className="text-foreground-secondary">
                      <span>{translations?.end ?? "End"}</span>
                      <span>:</span>
                    </span>
                    <TimeInput value={endTime} onChange={v => onTimeChange(v, "end")} aria-label="End date time" isDisabled={!range?.to} isRequired={props.required} />
                  </div>
                </div>}
              <div className="sticky bottom-0 border-t bg-background p-3 sm:flex sm:items-center sm:justify-between">
                <p className="tabular-nums text-foreground">
                  <span className="text-foreground-secondary">
                    <span>{translations?.range ?? "Range"}</span>
                    <span>:</span>
                  </span>{" "}
                  <span className="font-medium">{displayRange}</span>
                </p>
                <div className="mt-2 flex items-center gap-x-2 sm:mt-0">
                  <Button variant="secondary" className="h-8 w-full sm:w-fit" type="button" onClick={onCancel}>
                    {translations?.cancel ?? "Cancel"}
                  </Button>
                  <Button variant="primary" className="h-8 w-full sm:w-fit" type="button" onClick={onApply}>
                    {translations?.apply ?? "Apply"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CalendarPopover>
    </PopoverPrimitives.Root>;
};

//#region Preset Validation
// ============================================================================

const validatePresets = (presets: DateRangePreset[] | DatePreset[], rules: PickerProps) => {
  const {
    toYear,
    fromYear,
    fromMonth,
    toMonth,
    fromDay,
    toDay
  } = rules;
  if (presets && presets.length > 0) {
    const fromYearToUse = fromYear;
    const toYearToUse = toYear;
    for (const preset of presets) {
      if ("date" in preset) {
        const presetYear = preset.date.getFullYear();
        if (fromYear && presetYear < fromYear) {
          throw new Error(`Preset ${preset.label} is before fromYear ${fromYearToUse}.`);
        }
        if (toYear && presetYear > toYear) {
          throw new Error(`Preset ${preset.label} is after toYear ${toYearToUse}.`);
        }
        if (fromMonth) {
          const presetMonth = preset.date.getMonth();
          if (presetMonth < fromMonth.getMonth()) {
            throw new Error(`Preset ${preset.label} is before fromMonth ${fromMonth}.`);
          }
        }
        if (toMonth) {
          const presetMonth = preset.date.getMonth();
          if (presetMonth > toMonth.getMonth()) {
            throw new Error(`Preset ${preset.label} is after toMonth ${toMonth}.`);
          }
        }
        if (fromDay && preset.date < fromDay) {
          throw new Error(`Preset ${preset.label} is before fromDay ${fromDay}.`);
        }
        if (toDay && preset.date > toDay) {
          throw new Error(`Preset ${preset.label} is after toDay ${format(toDay, "MMM dd, yyyy")}.`);
        }
      }
      if ("dateRange" in preset) {
        const presetFromYear = preset.dateRange.from?.getFullYear();
        const presetToYear = preset.dateRange.to?.getFullYear();
        if (presetFromYear && fromYear && presetFromYear < fromYear) {
          throw new Error(`Preset ${preset.label}'s 'from' is before fromYear ${fromYearToUse}.`);
        }
        if (presetToYear && toYear && presetToYear > toYear) {
          throw new Error(`Preset ${preset.label}'s 'to' is after toYear ${toYearToUse}.`);
        }
        if (fromMonth) {
          const presetMonth = preset.dateRange.from?.getMonth();
          if (presetMonth && presetMonth < fromMonth.getMonth()) {
            throw new Error(`Preset ${preset.label}'s 'from' is before fromMonth ${format(fromMonth, "MMM, yyyy")}.`);
          }
        }
        if (toMonth) {
          const presetMonth = preset.dateRange.to?.getMonth();
          if (presetMonth && presetMonth > toMonth.getMonth()) {
            throw new Error(`Preset ${preset.label}'s 'to' is after toMonth ${format(toMonth, "MMM, yyyy")}.`);
          }
        }
        const presetFrom = preset.dateRange.from;
        if (fromDay && presetFrom && presetFrom < fromDay) {
          throw new Error(`Preset ${presetFrom}'s 'from' is before fromDay ${format(fromDay, "MMM dd, yyyy")}.`);
        }
        const presetTo = preset.dateRange.to;
        if (toDay && presetTo && presetTo > toDay) {
          throw new Error(`Preset ${preset.label}'s 'to' is after toDay ${format(toDay, "MMM dd, yyyy")}.`);
        }
      }
    }
  }
};

//#region Types & Exports
// ============================================================================

type SingleDatePickerProps = {
  presets?: DatePreset[];
  defaultValue?: Date;
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  onPopoverOpenChange?: (open: boolean) => void;
  /**
   * With `showTimePicker`, the clock the date and time are shown and picked
   * on, so a typed 8:00 AM means 8:00 AM there. Without it, the browser's.
   * Day limits (`disabledDays`, `fromDate`, `toDate`) are days on that clock:
   * pass `dayMarker(clinicClockFor(timeZone).today())`, not `new Date()`.
   */
  timeZone?: string;
} & PickerProps;
const DatePicker = ({
  presets,
  timeZone,
  value,
  defaultValue,
  onChange,
  ...props
}: SingleDatePickerProps) => {
  if (presets) {
    validatePresets(presets, props);
  }

  // The picker edits browser-local fields; on another clock it is handed that
  // clock's wall time in those fields and its result is converted back.
  const zone = props.showTimePicker ? timeZone : undefined;
  const valueMs = value?.getTime();
  const defaultValueMs = defaultValue?.getTime();
  const wallValue = React.useMemo(() => zone && value ? zonedWallClockDate(value, zone) : value,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [zone, valueMs]);
  const wallDefaultValue = React.useMemo(() => zone && defaultValue ? zonedWallClockDate(defaultValue, zone) : defaultValue,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [zone, defaultValueMs]);
  // The wall fields can't tell the two showings of a repeated fall-back hour
  // apart, so a time in that hour keeps the side of the change the value it
  // was given is on, as the time input does: applying the second 1:30 AM
  // unchanged must not move it an hour earlier.
  const referenceMs = valueMs ?? defaultValueMs;
  const handleChange = React.useCallback((date: Date | undefined) => {
    if (!zone || !date) return onChange?.(date);
    const instant = wallClockDateToInstant(date, zone);
    onChange?.(referenceMs === undefined ? instant : matchingOccurrence(instant, new Date(referenceMs), zone));
  }, [onChange, zone, referenceMs]);
  return <SingleDatePicker presets={presets} {...props as SingleProps} value={wallValue} defaultValue={wallDefaultValue} onChange={handleChange} />;
};
DatePicker.displayName = "DatePicker";
type RangeDatePickerProps = {
  presets?: DateRangePreset[];
  defaultValue?: DateRange;
  value?: DateRange;
  onChange?: (dateRange: DateRange | undefined) => void;
  ignoreYear?: boolean;
  max?: number;
} & PickerProps;
const DateRangePicker = t0 => {
  const $ = _c(6);
  let presets;
  let props;
  if ($[0] !== t0) {
    ({
      presets,
      ...props
    } = t0);
    if (presets) {
      validatePresets(presets, props);
    }
    $[0] = t0;
    $[1] = presets;
    $[2] = props;
  } else {
    presets = $[1];
    props = $[2];
  }
  const t1 = props as RangeProps;
  let t2;
  if ($[3] !== presets || $[4] !== t1) {
    t2 = <RangeDatePicker presets={presets} {...t1} />;
    $[3] = presets;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
DateRangePicker.displayName = "DateRangePicker";

//#region DatePickerWithYearDropdown
// ============================================================================

interface DatePickerProps {
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  maxDate?: Date;
  minDate?: Date;
  className?: string;
  value?: Date;
}
export const DatePickerWithYearDropdown: React.FC<DatePickerProps> = ({
  onChange,
  placeholder = "Select date or type MM/DD/YYYY",
  maxDate,
  minDate,
  className,
  value
}) => {
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(value);
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date | undefined>(value);

  // Sync internal state when value prop changes
  useEffect(() => {
    setSelectedDate(value);
    setMonth(value);
  }, [value]);
  const handleDateChange = (date: Date | undefined, closeModal: boolean = true) => {
    if (date) {
      // Extract date components in local time and recreate at noon
      // This avoids timezone issues when working with date-only values
      const year = date.getFullYear();
      const month_0 = date.getMonth();
      const day = date.getDate();
      const normalizedDate = new Date(year, month_0, day, 12, 0, 0);

      // Validate against min/max dates by comparing date-only components
      if (maxDate) {
        const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 23, 59, 59);
        if (normalizedDate > maxDateOnly) {
          return; // Don't update if date is after maxDate
        }
      }
      if (minDate) {
        const minDateOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 0, 0, 0);
        if (normalizedDate < minDateOnly) {
          return; // Don't update if date is before minDate
        }
      }
      setSelectedDate(normalizedDate);
      setMonth(normalizedDate);
      onChange(normalizedDate);
    } else {
      setSelectedDate(date);
      setMonth(date);
      onChange(date);
    }
    if (closeModal) {
      setOpen(false);
    }
  };
  const handleOpenCalendar = () => {
    setOpen(true);
  };
  return <PopoverPrimitives.Root open={open} onOpenChange={setOpen} modal>
      <PopoverPrimitives.Trigger asChild>
        <TypeableTrigger placeholder={placeholder} className={className} value={selectedDate}
      // A typed date never closes the calendar: a short date commits on
      // blur, and opening the calendar is what blurs the field
      onChange={date_0 => handleDateChange(date_0, false)} onOpenCalendar={handleOpenCalendar} />
      </PopoverPrimitives.Trigger>
      <CalendarPopover align="start" sideOffset={5} collisionPadding={20} avoidCollisions>
        <CalendarPrimitive mode="single" selected={selectedDate} onSelect={date_1 => {
        // Prevent deselection - if date is undefined, ignore it
        // This happens when clicking an already-selected date
        if (date_1 === undefined) {
          return;
        }
        handleDateChange(date_1);
      }} month={month} onMonthChange={setMonth} toDate={maxDate} fromDate={minDate} initialFocus components={{
        Caption: ({
          displayMonth
        }: {
          displayMonth: Date;
        }) => {
          const {
            goToMonth,
            nextMonth,
            previousMonth,
            displayMonths
          } = useNavigation();
          const {
            numberOfMonths
          } = useDayPicker();
          const displayIndex = displayMonths.findIndex(month_1 => isSameMonth(displayMonth, month_1));
          const isFirst = displayIndex === 0;
          const isLast = displayIndex === displayMonths.length - 1;
          const hideNextButton = numberOfMonths > 1 && (isFirst || !isLast);
          const hidePreviousButton = numberOfMonths > 1 && (isLast || !isFirst);
          const currentSelectedYear = getYear(displayMonth);
          const {
            first,
            last
          } = selectableYears();
          const years = Array.from({
            length: last - first + 1
          }, (_, i) => first + i);
          const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
          return <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <>
                      <SearchSelect value={currentSelectedYear.toString()} onValueChange={value_0 => {
                  const newYear = parseInt(value_0);
                  const newDate = setYear(displayMonth, newYear);
                  goToMonth(newDate);

                  // Also update the selected date if one exists
                  if (selectedDate) {
                    const updatedSelectedDate = setYear(selectedDate, newYear);
                    handleDateChange(updatedSelectedDate, false);
                  }
                }}>
                        <SearchSelectTrigger className="w-24">
                          <SearchSelectValue placeholder="Select year" />
                        </SearchSelectTrigger>
                        <SearchSelectContent>
                          {years.map(year_0 => <SearchSelectItem key={year_0} value={year_0.toString()}>
                              {year_0}
                            </SearchSelectItem>)}
                        </SearchSelectContent>
                      </SearchSelect>
                      <SearchSelect value={monthNames[displayMonth.getMonth()]} onValueChange={value_1 => {
                  const newMonthIndex = parseInt(value_1);
                  const newDate_0 = new Date(displayMonth);
                  newDate_0.setMonth(newMonthIndex);
                  goToMonth(newDate_0);

                  // Also update the selected date if one exists
                  if (selectedDate) {
                    const updatedSelectedDate_0 = new Date(selectedDate);
                    updatedSelectedDate_0.setMonth(newMonthIndex);
                    handleDateChange(updatedSelectedDate_0, false);
                  }
                }}>
                        <SearchSelectTrigger className="w-32">
                          <SearchSelectValue placeholder="Select month" />
                        </SearchSelectTrigger>
                        <SearchSelectContent>
                          {monthNames.map((month_2, index) => <SearchSelectItem key={month_2} value={index.toString()}>
                              {month_2}
                            </SearchSelectItem>)}
                        </SearchSelectContent>
                      </SearchSelect>
                    </>
                  </div>
                  <div className="flex items-center gap-1">
                    {!hidePreviousButton && <NavigationButton disabled={!previousMonth} aria-label="Go to previous month" onClick={() => previousMonth && goToMonth(previousMonth)} icon={RiArrowLeftSLine} />}
                    {!hideNextButton && <NavigationButton disabled={!nextMonth} aria-label="Go to next month" onClick={() => nextMonth && goToMonth(nextMonth)} icon={RiArrowRightSLine} />}
                  </div>
                </div>;
        }
      }} locale={enUS} />
      </CalendarPopover>
    </PopoverPrimitives.Root>;
};
export { DatePicker, DateRangePicker, type DatePreset, type DateRange, type DateRangePreset };
function _temp(e) {
  return e.preventDefault();
}
function _temp2(preset) {
  return "dateRange" in preset;
}
function _temp3(preset_0) {
  return "date" in preset_0;
}
function _temp4(date1, date2) {
  return date1.getDate() === date2.getDate() && date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
}